import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { XIcon } from "@heroicons/react/solid";

import { RegularLogin } from "./RegularLogin";

import logo from "@/assets/logo/promptlayer-cake-color.png";
import euFlag from "@/assets/flags/eu.svg";
import usFlag from "@/assets/flags/us.svg";
import { useAuth } from "@/context/auth-context";
import { AUTH_CONFIG } from "@/constants";
import { Viva } from "@/components/ui/icons";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { displayErrorToast } from "@/utils/toast";
import { resolveAuthenticatedLoginRedirect } from "@/utils/postLoginNavigation";

const companyLogoIcon = {
  viva: <Viva className="ml-2 h-[38px] w-[38px]" />,
};

// prod-eu is in cold standby, so the EU tab is hidden. Set to true when EU comes back.
const EU_REGION_ENABLED = false;

const LOGIN_REGIONS = [
  { id: "us", label: "US", flag: usFlag, host: "dashboard.promptlayer.com" },
  ...(EU_REGION_ENABLED
    ? [{ id: "eu", label: "EU", flag: euFlag, host: "dashboard.eu.promptlayer.com" }]
    : []),
] as const;

const getLoginRegion = () =>
  window.location.hostname === "dashboard.eu.promptlayer.com" ? "eu" : "us";

const Login = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyLogo } = AUTH_CONFIG;
  const isProductionLogin = LOGIN_REGIONS.some(region => region.host === window.location.hostname);
  const isLocalLoginRegionMockEnabled =
    import.meta.env.NODE_ENV !== "production" &&
    import.meta.env.VITE_SHOW_LOGIN_REGION_SWITCH === "true";
  const selectedRegion = getLoginRegion();

  const handleRegionChange = (regionId: string) => {
    const region = LOGIN_REGIONS.find(option => option.id === regionId);
    if (!region) {
      return;
    }

    window.location.assign(
      `https://${region.host}${window.location.pathname}${window.location.search}`,
    );
  };

  useEffect(() => {
    const inviteCode = searchParams.get("invite");
    if (inviteCode) {
      navigate(`/create-account/invitee/${inviteCode}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const ssoError = searchParams.get("sso_error");
    if (!ssoError) {
      return;
    }

    displayErrorToast(ssoError);

    const next = new URLSearchParams(searchParams);
    next.delete("sso_error");
    const remaining = next.toString();
    navigate(remaining ? `/login?${remaining}` : "/login", { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!auth?.userToken || !auth?.user) {
      return;
    }

    const redirectPath = resolveAuthenticatedLoginRedirect(auth.userToken, auth.user);
    if (redirectPath) {
      navigate(redirectPath);
    }
  }, [auth?.userToken, auth?.user, navigate]);

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-2">
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="flex items-center gap-2">
            <img src={logo} alt="logo" className="h-[52px] w-[52px]" />
            {companyLogo && (
              <div className="flex items-center gap-2">
                <XIcon className="h-4 w-4" />{" "}
                {companyLogoIcon[companyLogo as keyof typeof companyLogoIcon]}
              </div>
            )}
          </div>
          <span className="text-3xl font-bold text-gray-900">Sign In</span>
        </div>
      </div>
      {!AUTH_CONFIG.isOnPrem &&
        LOGIN_REGIONS.length > 1 &&
        (isProductionLogin || isLocalLoginRegionMockEnabled) && (
          <Tabs value={selectedRegion} onValueChange={handleRegionChange} className="mt-6 w-full">
            <TabsList className="grid h-12 w-full grid-cols-2 rounded-lg p-1">
              {LOGIN_REGIONS.map(region => (
                <TabsTrigger
                  key={region.id}
                  value={region.id}
                  className="flex h-10 items-center gap-2 rounded-md px-4 text-sm"
                >
                  <img src={region.flag} alt="" className="h-5 w-7 object-contain" />
                  <span className="text-sm font-medium">{region.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
      <RegularLogin />
    </>
  );
};

export default Login;
